import { createContext, useContext, useSyncExternalStore } from 'react'

// Public API exposed to consumers
export type WorkflowContextProps = {
  isDirty: boolean
  saveWorkflow: () => Promise<void>
}

// Internal context - provides subscribe and getSnapshot for external store pattern
type WorkflowContextInternal = {
  subscribe: (callback: () => void) => () => void
  getSnapshot: () => WorkflowContextProps
  updateState: (state: WorkflowContextProps) => void
}

const WorkflowContext = createContext<WorkflowContextInternal | null>(null)

// Provider component
export const WorkflowProvider = ({ children }: { children: React.ReactNode }) => {
  // Store state and subscribers - WorkflowContainer will update this
  let currentState: WorkflowContextProps = {
    isDirty: false,
    saveWorkflow: async () => {},
  }
  const subscribers = new Set<() => void>()

  const subscribe = (callback: () => void) => {
    subscribers.add(callback)
    return () => subscribers.delete(callback)
  }

  const getSnapshot = () => currentState

  const updateState = (state: WorkflowContextProps) => {
    currentState = state
    // Notify all subscribers
    subscribers.forEach((callback) => callback())
  }

  return (
    <WorkflowContext.Provider value={{ subscribe, getSnapshot, updateState }}>
      {children}
    </WorkflowContext.Provider>
  )
}

// Hook for consumers - uses useSyncExternalStore for automatic re-renders
// instead of duplicating state in the provider
export const useWorkflow = (): WorkflowContextProps => {
  const context = useContext(WorkflowContext)
  if (!context) {
    throw new Error('useWorkflow must be used within WorkflowProvider')
  }
  return useSyncExternalStore(context.subscribe, context.getSnapshot)
}

// Hook for WorkflowContainer to update state
export const useWorkflowUpdate = () => {
  const context = useContext(WorkflowContext)
  return context?.updateState ?? null
}
